import { useCallback, useEffect, useState } from 'react'
import { useCreatePaymentSessionMutation, useGetPaymentStatusQuery } from '@/core/graphql/hooks'
import { getEnvVar } from '@/lib/env'

interface UseStripePaymentProps {
  orderId: string
  successUrl?: string
  cancelUrl?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function useStripePayment({ orderId, successUrl: customSuccessUrl, cancelUrl: customCancelUrl, onSuccess, onError }: UseStripePaymentProps) {
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // GraphQL mutations and queries
  const [createPaymentSession] = useCreatePaymentSessionMutation()
  const { data: paymentStatus, loading: isCheckingStatus } = useGetPaymentStatusQuery({
    variables: { payload: { orderId } },
    // Poll every 2 seconds while checking payment status
    pollInterval: 2000,
    // Skip if we're redirecting to Stripe
    skip: isRedirecting,
  })

  // Generate default URLs based on current route if not provided
  const getPaymentUrls = useCallback(() => {
    // Get the base URL from window location or fallback to environment variables
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:4000'

    // Get current pathname to extract meetingLink if we're in the booking flow
    let meetingLinkSlug = ''
    if (typeof window !== 'undefined') {
      const path = window.location.pathname
      const match = path.match(/\/meeting-links\/([^/]+)/)
      if (match && match[1]) {
        meetingLinkSlug = match[1]
      }
    }

    // Default URLs if we couldn't determine a meeting link
    let defaultSuccessUrl = `${baseUrl}/payment/success?orderId=${orderId}`
    let defaultCancelUrl = `${baseUrl}/payment/cancel?orderId=${orderId}`

    // If we detected a meeting link, use the new URL pattern
    if (meetingLinkSlug) {
      defaultSuccessUrl = `${baseUrl}/meeting-links/${meetingLinkSlug}/booking/payment/success?orderId=${orderId}`
      defaultCancelUrl = `${baseUrl}/meeting-links/${meetingLinkSlug}/booking/payment/cancel?orderId=${orderId}`
    }

    // Use custom URLs if provided, otherwise use our default ones
    const successUrl = customSuccessUrl || defaultSuccessUrl
    const cancelUrl = customCancelUrl || defaultCancelUrl

    return { successUrl, cancelUrl }
  }, [orderId, customSuccessUrl, customCancelUrl])

  // Initialize payment and redirect to Stripe Checkout
  const initializePayment = useCallback(async () => {
    try {
      setIsRedirecting(true)
      setError(null)

      // Get the URLs to use for success and cancellation
      const { successUrl, cancelUrl } = getPaymentUrls()

      const { data } = await createPaymentSession({
        variables: {
          payload: {
            orderId,
            successUrl,
            cancelUrl,
          },
        },
      })

      if (!data?.createPaymentSession.sessionId) {
        throw new Error('Failed to create payment session')
      }

      // Get the publishable key from our env utility
      const stripeKey = getEnvVar('NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY')

      if (!stripeKey) {
        console.error('Stripe publishable key is missing')
        throw new Error('Payment configuration error: Missing Stripe key')
      }

      // Load the Stripe Checkout page
      const stripe = await import('@stripe/stripe-js').then((module) => module.loadStripe(stripeKey!))

      if (!stripe) {
        throw new Error('Failed to load Stripe')
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: data.createPaymentSession.sessionId,
      })

      if (error) {
        throw error
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Payment initialization failed'))
      onError?.(err instanceof Error ? err : new Error('Payment initialization failed'))
    } finally {
      setIsRedirecting(false)
    }
  }, [createPaymentSession, orderId, getPaymentUrls, onError])

  // Handle payment status changes
  useEffect(() => {
    if (!isRedirecting && paymentStatus?.getPaymentStatus.paid) {
      onSuccess?.()
    }
  }, [paymentStatus, isRedirecting, onSuccess])

  return {
    initializePayment,
    isRedirecting,
    isCheckingStatus,
    error,
    paymentStatus: paymentStatus?.getPaymentStatus,
  }
}
